import React from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form } from "react-bootstrap";
import logger from "../../../utils/logging";
import { TemplateFile, TemplateFileIO } from "../../../models/Templates";
import { FieldValidation } from "./useCreateWizard";
import { BaseValidatedFormGroup, ValidatedCombobox } from "./validation";

interface StepSelectTemplateProps {
    templates: TemplateFile[] | null;
    selectedTemplate: TemplateFile | null;
    onTemplateChange: (template: TemplateFile | null) => void;
    onTemplatePreview: (template: TemplateFile) => void;
    validation?: FieldValidation;
}

const pdfTemplateTextFieldProvider = (item: unknown): string => {
    if (TemplateFileIO.is(item)) {
        return item.name;
    }
    if (typeof item === "string") {
        return item;
    }
    if (item == null) {
        return "";
    }
    logger.error("unknown type for template item", item);
    return "[unknown]";
};

const formatTemplate = (
    template: TemplateFile | string | null,
    t: any,
    onPreview?: (template: TemplateFile) => void,
): React.ReactElement => {
    if (TemplateFileIO.is(template)) {
        return (
            <span>
                <strong>{template.name}</strong>&nbsp; (
                <button
                    type="button"
                    className="btn btn-link p-0 align-baseline"
                    onClick={() => onPreview && onPreview(template)}
                >
                    {t("selectTemplate.preview")}
                </button>
                )
            </span>
        );
    }
    if (typeof template === "string") {
        return <>{template}</>;
    } else {
        return <>-</>;
    }
};

const idProvider = (template: unknown): string => {
    if (TemplateFileIO.is(template)) {
        return template.id;
    }
    if (typeof template === "string") {
        return template;
    }
    if (template == null) {
        return "<null>";
    }
    logger.error("unknown type for template item", template);
    return "[unknown]";
};

const StepSelectTemplate: React.FC<StepSelectTemplateProps> = ({
    templates,
    selectedTemplate,
    onTemplateChange,
    onTemplatePreview,
    validation,
}) => {
    const { t } = useTranslation("wizard");

    return (
        <div className="step-select-template">
            <h3 className="mb-4">{t("selectTemplate.stepTitle")}</h3>
            <p className="text-muted small mb-4">{t("selectTemplate.description")}</p>

            <Row>
                <Col md={8}>
                    <BaseValidatedFormGroup
                        className="form-group"
                        controlId="formGroupPdfTemplate_input"
                        validation={validation}
                    >
                        <Form.Label>{t("selectTemplate.label")}</Form.Label>
                        <ValidatedCombobox<TemplateFile>
                            id="formGroupPdfTemplate"
                            busy={templates == null}
                            data={templates == null ? [] : templates}
                            value={selectedTemplate || undefined}
                            textField={pdfTemplateTextFieldProvider}
                            dataKey={idProvider}
                            renderListItem={({ item }) => formatTemplate(item, t, onTemplatePreview)}
                            onChange={(value) => {
                                logger.debug("set new selected template:", value);
                                onTemplateChange(value || null);
                            }}
                            placeholder={t("selectTemplate.placeholder")}
                            validation={validation}
                        />
                        <Form.Text className="text-muted">{t("selectTemplate.helpText")}</Form.Text>
                    </BaseValidatedFormGroup>
                </Col>
            </Row>

            {selectedTemplate && (
                <Row className="mt-4">
                    <Col md={8}>
                        <div className="alert alert-info">
                            <h5>{t("selectTemplate.selectedTitle")}</h5>
                            <p className="mb-1">
                                <strong>{t("selectTemplate.selectedName")}</strong> {selectedTemplate.name}
                            </p>
                            <p className="mb-1">
                                <strong>{t("selectTemplate.selectedFile")}</strong>
                                <button
                                    type="button"
                                    className="btn btn-link p-0 align-baseline ms-2"
                                    onClick={() => onTemplatePreview(selectedTemplate)}
                                >
                                    {t("selectTemplate.openTemplate")}
                                </button>
                            </p>
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default StepSelectTemplate;
